import React from 'react';
import { WalkInToggle } from '@parsina/framework';
import { useTranslation } from '../../../i18n';
import { fmtJalaliShort } from '../helpers';

/**
 * «خارج از نوبت» — the booking that has no hour.
 *
 * The desk's third answer to "when?", beside a slot and «اولین نوبت»: the patient who
 * is squeezed in. Writing one down used to mean giving it an hour it does not have,
 * which puts a fiction on the grid, makes the day read busier than it is, and eats a
 * slot somebody with a real appointment could have had.
 *
 * It means what the tab it is used from can mean: in «انتخاب دستی» "that day, no
 * hour"; in the auto tabs "today, and they are standing here".
 *
 * Drawn as the kit's `WalkInToggle` (PM-0056) — ONE switch row inside the «تاریخ و
 * ساعت» box, outside the folded time options, the same row the doctor's «ثبت نوبت»
 * draws. Where it sits says how likely it is: while the doctor still has a free slot
 * it FOLLOWS the suggestion («یا»); when the day has none left it LEADS as
 * «پیشنهاد». While it is on, its own line says what will happen — there is no
 * separate notice.
 *
 * Both clinic modals draw it, so it lives here rather than twice.
 */

/** DAY in the manual tab, NOW in the auto tabs. Sent verbatim as `untimed`. */
export const UNTIMED_DAY = 'DAY';
export const UNTIMED_NOW = 'NOW';

/**
 * The toggle's state, and what to send.
 *
 * Off every time the modal opens and deliberately not remembered: fitting somebody in
 * is a decision about ONE patient, and a toggle that stayed on would quietly stop the
 * desk giving anybody an hour.
 *
 * `mode` is the tab's answer — so switching tabs re-reads it rather than carrying a
 * meaning from the tab it was ticked on.
 */
export function useUntimed(mode) {
  const [on, setOn] = React.useState(false);
  return {
    on,
    setOn,
    /** What `createAppointment` should carry, or null for an ordinary booking. */
    value: on ? mode : null,
  };
}

/**
 * Whether the row LEADS: the doctor has nothing free where this booking would land.
 * NOW leads when today's day-scoped search came back empty (or a booking just lost
 * today's last slot); DAY leads when the manual grid shows the picked day full.
 */
export function untimedSuggested({ mode, slotMode, dayIsToday, previewEmpty, allBooked, todayFull }) {
  if (mode === UNTIMED_NOW) return !!todayFull || (slotMode === 'firstOfDay' && dayIsToday && !!previewEmpty);
  return !!allBooked;
}

/**
 * The row. `compact` is the phone form: one line, and a hint only once it adds
 * something (on, or suggested).
 */
export function UntimedRow({ untimed, mode, suggested = false, separated = false, compact = false, selectedGDate }) {
  const { t, formatDate, locale } = useTranslation();
  const now = mode === UNTIMED_NOW;
  const on = untimed.on;
  let hint;
  if (on) {
    hint = now
      ? t(compact ? 'calendar.slots.untimedNowNoticeShort' : 'calendar.slots.untimedNowNotice')
      : t(compact ? 'calendar.slots.untimedNoticeShort' : 'calendar.slots.untimedNotice');
  } else if (suggested) {
    hint = now
      ? t(compact ? 'calendar.slots.untimedLeadShort' : 'calendar.slots.untimedLead')
      : t(compact ? 'calendar.slots.untimedLeadDayShort' : 'calendar.slots.untimedLeadDay');
  } else if (!compact) {
    hint = now
      ? t('calendar.slots.untimedNowHint')
      : t('calendar.slots.untimedHint', { date: fmtJalaliShort(selectedGDate, formatDate) });
  } else {
    hint = null;
  }
  return (
    <WalkInToggle
      checked={on}
      onChange={untimed.setOn}
      suggested={suggested}
      separated={separated && !on}
      compact={compact}
      title={now ? t('calendar.slots.untimedNow') : t('calendar.slots.untimed')}
      hint={hint}
      locale={locale}
      testId="untimed-toggle"
    />
  );
}
